import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import Navbar from '../components/Navbar';
import { GoldBtn } from '../components/UI';
import { supabase } from '../lib/supabase';
import {
  getChatMessages, sendChatMessage, deleteChatMessage,
  getActivePolls, createPoll, votePoll, removePollVote, closePoll,
  getOnlineUsers, updatePresence,
} from '../lib/supabase';

// ── Avatar (same as Navbar) ───────────────────────────────────
function Avatar({ username, size = 26 }) {
  const colors = ['#8B5CF6','#3B82F6','#10B981','#F59E0B','#EF4444','#EC4899','#14B8A6'];
  const bg = colors[(username?.charCodeAt(0) || 0) % colors.length];
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', background: bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontSize: size * 0.42, fontWeight: 700,
      fontFamily: "'DM Mono', monospace", flexShrink: 0,
    }}>
      {(username?.[0] || '?').toUpperCase()}
    </div>
  );
}

// ── Create Poll Modal ─────────────────────────────────────────
function CreatePollModal({ open, onClose, onCreated, userId }) {
  const [question, setQuestion] = useState('');
  const [options,  setOptions]  = useState(['', '']);
  const [saving,   setSaving]   = useState(false);
  const [err,      setErr]      = useState('');

  if (!open) return null;

  const addOption    = () => options.length < 6 && setOptions(p => [...p, '']);
  const removeOption = i  => options.length > 2 && setOptions(p => p.filter((_, j) => j !== i));
  const setOption    = (i, v) => setOptions(p => p.map((x, j) => j === i ? v : x));

  const handleCreate = async () => {
    const q = question.trim();
    const opts = options.map(o => o.trim()).filter(Boolean);
    if (!q)        { setErr('Please enter a question.'); return; }
    if (opts.length < 2) { setErr('Please add at least 2 options.'); return; }
    setSaving(true);
    const error = await createPoll(userId, q, opts);
    setSaving(false);
    if (error) { setErr(error.message); return; }
    setQuestion(''); setOptions(['', '']); setErr('');
    onCreated(); onClose();
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 300,
      background: 'rgba(4,4,6,0.88)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 16px',
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border2)',
        borderRadius: 14, width: '100%', maxWidth: 460, padding: 24,
        boxShadow: '0 32px 80px rgba(0,0,0,0.7)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ fontFamily: "'Playfair Display', serif", color: 'var(--gold)', fontSize: 20, margin: 0 }}>
            📊 Create Poll
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <div style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 6 }}>Poll Question</div>
            <input value={question} onChange={e => setQuestion(e.target.value)}
              placeholder="e.g. Best Malayalam thriller of all time?"
              style={{ width: '100%', background: 'var(--surface2)', border: '1px solid var(--border2)', borderRadius: 7, color: 'var(--text)', padding: '10px 14px', fontSize: 15, fontFamily: "'Crimson Text', serif", outline: 'none' }}
              onFocus={e => e.target.style.borderColor = 'var(--gold-dim)'}
              onBlur={e => e.target.style.borderColor = 'var(--border2)'}
            />
          </div>

          <div>
            <div style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 8 }}>Options (2–6)</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {options.map((opt, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input value={opt} onChange={e => setOption(i, e.target.value)}
                    placeholder={`Option ${i + 1}`}
                    style={{ flex: 1, background: 'var(--surface2)', border: '1px solid var(--border2)', borderRadius: 7, color: 'var(--text)', padding: '9px 12px', fontSize: 14, fontFamily: "'Crimson Text', serif", outline: 'none' }}
                    onFocus={e => e.target.style.borderColor = 'var(--gold-dim)'}
                    onBlur={e => e.target.style.borderColor = 'var(--border2)'}
                  />
                  {options.length > 2 && (
                    <button onClick={() => removeOption(i)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 18, cursor: 'pointer', padding: '0 4px' }}>×</button>
                  )}
                </div>
              ))}
            </div>
            {options.length < 6 && (
              <button onClick={addOption} style={{ marginTop: 8, background: 'none', border: '1px dashed var(--border2)', borderRadius: 7, color: 'var(--text-muted)', padding: '7px 14px', fontSize: 13, cursor: 'pointer', width: '100%' }}>
                + Add Option
              </button>
            )}
          </div>

          {err && <div style={{ color: 'var(--red)', fontSize: 13, padding: '8px 12px', background: 'rgba(192,57,43,0.1)', borderRadius: 6 }}>{err}</div>}

          <GoldBtn onClick={handleCreate} disabled={saving}>
            {saving ? 'Creating…' : 'Create Poll'}
          </GoldBtn>
        </div>
      </div>
    </div>
  );
}

// ── Poll Card ─────────────────────────────────────────────────
function PollCard({ poll, userId, isAdmin, onVoted, onClosed }) {
  const votes     = poll.chat_poll_votes || [];
  const myVote    = votes.find(v => v.user_id === userId);
  const totalVotes = votes.length;

  const handleVote = async (idx) => {
    if (!userId) return;
    // If clicking same option — deselect (delete vote)
    if (myVote?.option_idx === idx) {
      await removePollVote(poll.id, userId);
    } else {
      // Change vote or cast new vote
      await votePoll(poll.id, userId, idx);
    }
    onVoted();
  };

  const handleClose = async () => {
    await closePoll(poll.id);
    onClosed();
  };

  return (
    <div style={{
      background: 'var(--surface2)', border: '1px solid rgba(212,175,55,0.2)',
      borderRadius: 12, padding: '14px 16px', margin: '8px 0',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 8 }}>
        <div style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)', fontSize: 15, fontWeight: 600, lineHeight: 1.4 }}>
          📊 {poll.question}
        </div>
        {isAdmin && (
          <button onClick={handleClose} style={{ background: 'none', border: '1px solid var(--border2)', borderRadius: 5, color: 'var(--text-muted)', fontSize: 11, padding: '3px 8px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            Close Poll
          </button>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {poll.options.map((opt, idx) => {
          const count   = votes.filter(v => v.option_idx === idx).length;
          const pct     = totalVotes ? Math.round(count / totalVotes * 100) : 0;
          const voted   = myVote?.option_idx === idx;
          const hasVoted = !!myVote;

          return (
            <button key={idx} onClick={() => userId && handleVote(idx)} style={{
              position: 'relative', overflow: 'hidden',
              background: voted ? 'rgba(212,175,55,0.12)' : 'var(--surface)',
              border: `1.5px solid ${voted ? 'var(--gold)' : 'var(--border2)'}`,
              borderRadius: 8, padding: '9px 12px', cursor: userId ? 'pointer' : 'default',
              textAlign: 'left', transition: 'all 0.18s',
            }}>
              {/* Progress bar */}
              {hasVoted && (
                <div style={{
                  position: 'absolute', left: 0, top: 0, bottom: 0,
                  width: `${pct}%`, background: voted ? 'rgba(212,175,55,0.08)' : 'rgba(255,255,255,0.02)',
                  transition: 'width 0.4s ease',
                }} />
              )}
              <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <span style={{ color: voted ? 'var(--gold)' : 'var(--text)', fontSize: 14 }}>{opt}</span>
                {hasVoted && (
                  <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: voted ? 'var(--gold)' : 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    {count} · {pct}%
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <div style={{ marginTop: 8, color: 'var(--text-muted)', fontSize: 11, fontFamily: "'DM Mono', monospace" }}>
        {totalVotes} vote{totalVotes !== 1 ? 's' : ''}{userId ? (myVote ? ' · click to change or deselect' : ' · tap to vote') : ''}
      </div>
    </div>
  );
}

// ── Main ChatPage ─────────────────────────────────────────────
export default function ChatPage({ onAuthOpen, onAdminClick, onFilmClick, onBattleClick, onRankingsClick, onTasteClick }) {
  const { user, profile, banned } = useAuth();
  const [messages,     setMessages]     = useState([]);
  const [polls,        setPolls]        = useState([]);
  const [onlineUsers,  setOnlineUsers]  = useState([]);
  const [text,         setText]         = useState('');
  const [sending,      setSending]      = useState(false);
  const [showPollModal,setShowPollModal]= useState(false);
  const [tab,          setTab]          = useState('chat'); // 'chat' | 'polls'
  const bottomRef  = useRef(null);
  const presenceRef = useRef(null);
  const isAdmin = profile?.role === 'admin';

  const loadMessages = useCallback(async () => {
    const msgs = await getChatMessages(80);
    setMessages(msgs);
  }, []);

  const loadPolls = useCallback(async () => {
    const p = await getActivePolls();
    setPolls(p);
  }, []);

  const loadOnline = useCallback(async () => {
    const u = await getOnlineUsers();
    setOnlineUsers(u);
  }, []);

  // Initial load
  useEffect(() => {
    loadMessages();
    loadPolls();
    loadOnline();
  }, [loadMessages, loadPolls, loadOnline]);

  // Real-time subscriptions — single channel for all chat tables
  useEffect(() => {
    const channel = supabase
      .channel('critiqr-chat-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'chat_messages' }, () => loadMessages())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'chat_polls' }, () => loadPolls())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'chat_poll_votes' }, () => loadPolls())
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [loadMessages, loadPolls]);

  // Presence — update every 60s while on page
  useEffect(() => {
    if (!user) return;
    updatePresence(user.id);
    loadOnline();
    presenceRef.current = setInterval(() => {
      updatePresence(user.id);
      loadOnline();
    }, 60000);
    return () => clearInterval(presenceRef.current);
  }, [user, loadOnline]);

  // Scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const msg = text.trim();
    if (!msg || !user || banned) return;
    if (msg.length > 500) return;
    setSending(true);
    setText('');
    await sendChatMessage(user.id, msg);
    setSending(false);
  };

  const handleDelete = async (id) => {
    await deleteChatMessage(id);
    loadMessages();
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  // Group messages — show avatar/name only when sender changes
  const grouped = messages.map((msg, i) => ({
    ...msg,
    showHeader: i === 0 || messages[i - 1].user_id !== msg.user_id,
  }));

  return (
    <div style={{ minHeight: '100vh', background: 'var(--black)', display: 'flex', flexDirection: 'column' }}>
      <Navbar
        onAuthOpen={onAuthOpen}
        onAdminClick={onAdminClick}
        onRequestFilm={null}
      />

      <div style={{ flex: 1, maxWidth: 1100, width: '100%', margin: '0 auto', padding: '24px 16px', display: 'flex', gap: 20, alignItems: 'flex-start' }}>

        {/* ── Main chat area ── */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0 }}>

          {/* Header */}
          <div style={{ marginBottom: 20 }}>
            <h1 style={{ fontFamily: "'Playfair Display', serif", color: 'var(--text)', fontSize: 'clamp(22px,4vw,30px)', fontWeight: 700, marginBottom: 4 }}>
              🎬 Film Chat
            </h1>
            <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
              Discuss films, get recommendations, vote in polls.
            </p>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', marginBottom: 16 }}>
            {[['chat', '💬 Chat'], ['polls', `📊 Polls${polls.length ? ` (${polls.length})` : ''}`]].map(([t, label]) => (
              <button key={t} onClick={() => setTab(t)} style={{
                padding: '10px 20px', background: 'none', border: 'none',
                borderBottom: tab === t ? '2px solid var(--gold)' : '2px solid transparent',
                color: tab === t ? 'var(--gold)' : 'var(--text-muted)',
                fontSize: 15, cursor: 'pointer', fontFamily: "'Crimson Text', serif",
                fontWeight: tab === t ? 600 : 400, transition: 'all 0.15s',
              }}>{label}</button>
            ))}
            {isAdmin && tab === 'polls' && (
              <button onClick={() => setShowPollModal(true)} style={{
                marginLeft: 'auto', padding: '8px 16px', background: 'rgba(212,175,55,0.1)',
                border: '1px solid var(--gold-dim)', borderRadius: 6,
                color: 'var(--gold)', fontSize: 13, cursor: 'pointer',
                fontFamily: "'Crimson Text', serif", alignSelf: 'center',
              }}>+ New Poll</button>
            )}
          </div>

          {/* ── CHAT TAB ── */}
          {tab === 'chat' && (
            <>
              {/* Messages area */}
              <div style={{
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 12, padding: '16px', height: 480,
                overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2,
              }}>
                {messages.length === 0 && (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', textAlign: 'center' }}>
                    <div style={{ fontSize: 40, marginBottom: 10 }}>🎭</div>
                    <div style={{ fontSize: 15 }}>No messages yet. Start the conversation!</div>
                  </div>
                )}

                {grouped.map(msg => {
                  const isMe    = msg.user_id === user?.id;
                  const msgAdmin = msg.profiles?.role === 'admin';
                  const canDelete = isMe || isAdmin;

                  return (
                    <div key={msg.id} style={{
                      display: 'flex', gap: 10, padding: '4px 6px',
                      marginTop: msg.showHeader ? 10 : 0,
                      borderRadius: 8,
                      flexDirection: isMe ? 'row-reverse' : 'row',
                      alignItems: 'flex-start',
                    }}
                      className="chat-msg"
                    >
                      {msg.showHeader && (
                        <Avatar username={msg.profiles?.username} size={28} />
                      )}
                      {!msg.showHeader && <div style={{ width: 28, flexShrink: 0 }} />}

                      <div style={{ maxWidth: '72%', display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start' }}>
                        {msg.showHeader && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3, flexDirection: isMe ? 'row-reverse' : 'row' }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: isMe ? 'var(--gold)' : 'var(--text-dim)' }}>
                              {isMe ? 'You' : msg.profiles?.username || 'Unknown'}
                            </span>
                            {msgAdmin && (
                              <span style={{ fontSize: 10, background: 'rgba(212,175,55,0.15)', color: 'var(--gold)', borderRadius: 4, padding: '1px 5px' }}>Admin</span>
                            )}
                            <span style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: "'DM Mono', monospace" }}>
                              {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        )}

                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexDirection: isMe ? 'row-reverse' : 'row' }}>
                          <div style={{
                            background: isMe ? 'rgba(212,175,55,0.12)' : 'var(--surface2)',
                            border: `1px solid ${isMe ? 'rgba(212,175,55,0.25)' : 'var(--border)'}`,
                            borderRadius: isMe ? '12px 4px 12px 12px' : '4px 12px 12px 12px',
                            padding: '8px 12px', fontSize: 15, color: 'var(--text)', lineHeight: 1.5,
                            wordBreak: 'break-word',
                          }}>
                            {msg.message}
                          </div>
                          {canDelete && (
                            <button onClick={() => handleDelete(msg.id)} style={{
                              background: 'none', border: 'none', color: 'var(--text-muted)',
                              fontSize: 13, cursor: 'pointer', opacity: 0.4, padding: 2,
                              transition: 'opacity 0.15s',
                            }}
                              onMouseEnter={e => e.currentTarget.style.opacity = 1}
                              onMouseLeave={e => e.currentTarget.style.opacity = 0.4}
                              title="Delete message"
                            >🗑</button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>

              {/* Message input */}
              <div style={{ marginTop: 12 }}>
                {!user ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 16px' }}>
                    <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>Log in to join the conversation</span>
                    <GoldBtn small onClick={onAuthOpen}>Log In</GoldBtn>
                  </div>
                ) : banned ? (
                  <div style={{ background: 'rgba(192,57,43,0.1)', border: '1px solid rgba(192,57,43,0.2)', borderRadius: 10, padding: '12px 16px', color: 'var(--red)', fontSize: 14 }}>
                    Your account is suspended. You cannot send messages.
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                    <textarea
                      value={text}
                      onChange={e => setText(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Type a message… (Enter to send, Shift+Enter for new line)"
                      maxLength={500}
                      rows={2}
                      style={{
                        flex: 1, background: 'var(--surface)', border: '1px solid var(--border2)',
                        borderRadius: 10, color: 'var(--text)', padding: '12px 14px',
                        fontSize: 15, resize: 'none', fontFamily: "'Crimson Text', serif",
                        lineHeight: 1.5, outline: 'none',
                      }}
                      onFocus={e => e.target.style.borderColor = 'var(--gold-dim)'}
                      onBlur={e => e.target.style.borderColor = 'var(--border2)'}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                      <GoldBtn onClick={handleSend} disabled={sending || !text.trim()} style={{ whiteSpace: 'nowrap' }}>
                        {sending ? '…' : '➤ Send'}
                      </GoldBtn>
                      <span style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: "'DM Mono', monospace" }}>
                        {text.length}/500
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── POLLS TAB ── */}
          {tab === 'polls' && (
            <div>
              {polls.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
                  <div style={{ fontSize: 40, marginBottom: 10 }}>📊</div>
                  <div style={{ fontSize: 15, marginBottom: 16 }}>No active polls right now.</div>
                  {isAdmin && (
                    <GoldBtn onClick={() => setShowPollModal(true)}>+ Create First Poll</GoldBtn>
                  )}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {polls.map(poll => (
                    <PollCard
                      key={poll.id}
                      poll={poll}
                      userId={user?.id}
                      isAdmin={isAdmin}
                      onVoted={loadPolls}
                      onClosed={loadPolls}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Online Users Sidebar (desktop only) ── */}
        <div className="chat-sidebar" style={{ width: 200, flexShrink: 0 }}>
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 12, padding: '16px',
            position: 'sticky', top: 80,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--green)', boxShadow: '0 0 6px var(--green)' }} />
              <span style={{ color: 'var(--text-dim)', fontSize: 13, fontWeight: 600 }}>
                Online ({onlineUsers.length})
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {onlineUsers.length === 0 && (
                <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No one online</div>
              )}
              {onlineUsers.map(u => (
                <div key={u.user_id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ position: 'relative' }}>
                    <Avatar username={u.profiles?.username} size={26} />
                    <div style={{ position: 'absolute', bottom: 0, right: 0, width: 7, height: 7, borderRadius: '50%', background: 'var(--green)', border: '1.5px solid var(--surface)' }} />
                  </div>
                  <div>
                    <div style={{ color: 'var(--text)', fontSize: 13, lineHeight: 1.2 }}>
                      {u.profiles?.username || 'Unknown'}
                      {u.user_id === user?.id && <span style={{ color: 'var(--text-muted)', fontSize: 11 }}> (you)</span>}
                    </div>
                    {u.profiles?.role === 'admin' && (
                      <div style={{ color: 'var(--gold)', fontSize: 10 }}>★ Admin</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <CreatePollModal
        open={showPollModal}
        onClose={() => setShowPollModal(false)}
        onCreated={loadPolls}
        userId={user?.id}
      />

      <style>{`
        @media (max-width: 700px) {
          .chat-sidebar { display: none !important; }
        }
        .chat-msg:hover .delete-btn { opacity: 1 !important; }
      `}</style>
    </div>
  );
}
